import React, { useContext } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { LogoutOutlined } from "@mui/icons-material"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import { Avatar, Box, Drawer, List, ListItemAvatar, ListItemButton, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import NativeLayerContext from "../contexts/NativeLayerContext"
import ShiftContext from "../contexts/ShiftContext"
import { EFunctionalityOverrides, ENativeLayerOutgoingEventType } from "../utils/nativeLayerCommunication/interfaces"
import { sendNativeLayerEvent } from "../utils/nativeLayerCommunication/sendNativeLayerEvent"

interface DrawerComponentProps {
    open: boolean
    onClose: () => void
}

export const BottomBarDrawer: React.FC<DrawerComponentProps> = ({ open, onClose }) => {
    const { t } = useTranslation()
    const shiftContext = useContext(ShiftContext)
    const nativeLayerContext = useContext(NativeLayerContext)
    const navigate = useNavigate()
    const { logout } = useAuth0()

    function handleLogout(): void {
        if (shiftContext?.vehicleId) {
            navigate("/vehicle", { state: { logout: true, odometer: true } })
        } else {
            // Clear all localStorage except 'completedJobGroups'
            const completedJobGroups = localStorage.getItem("completedJobGroups")
            localStorage.clear()
            if (completedJobGroups) {
                localStorage.setItem("completedJobGroups", completedJobGroups)
            }
            if (nativeLayerContext.nativeFunctionalityOverrides?.includes(EFunctionalityOverrides.AUTH)) {
                sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.LOGOUT })
            } else {
                logout({ logoutParams: { returnTo: window.location.origin } })
            }
        }
        onClose()
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Drawer
                anchor={"right"}
                open={open}
                onClose={() => onClose()}
                PaperProps={{
                    sx: {
                        bgcolor: "background.paper",
                        px: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        height: "100%",
                    },
                }}
            >
                <List>
                    <ListItemButton
                        onClick={() => {
                            onClose()
                            navigate("/")
                        }}
                        sx={{
                            "&:hover": {
                                bgcolor: "primary.light",
                            },
                            borderBottom: 1,
                            py: "4px",
                        }}
                    >
                        <ListItemAvatar>
                            <Avatar alt={"Allotrac"} sx={{ width: 40, height: 40 }} src="./logo.png" />
                        </ListItemAvatar>
                        <Typography> {t("sideBar.mobile.home")}</Typography>
                    </ListItemButton>

                    {shiftContext && shiftContext?.vehicleId && (
                        <ListItemButton
                            onClick={() => {
                                onClose()
                                navigate("/vehicle")
                            }}
                            sx={{
                                borderBottom: 1,
                                "&:hover": {
                                    bgcolor: "primary.light", // Light color on hover
                                },
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar sx={{ backgroundColor: "white" }}>
                                    <LocalShippingIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <Typography>{t("sideBar.mobile.vehicle")}</Typography>
                        </ListItemButton>
                    )}

                    <ListItemButton
                        onClick={() => {
                            handleLogout()
                        }}
                        sx={{
                            borderBottom: 1,
                            "&:hover": {
                                bgcolor: "primary.light", // Light color for logout on hover
                            },
                        }}
                    >
                        <ListItemAvatar>
                            <Avatar sx={{ backgroundColor: "white" }}>
                                <LogoutOutlined />
                            </Avatar>
                        </ListItemAvatar>
                        <Typography>{t("sideBar.mobile.logout")}</Typography>
                    </ListItemButton>
                </List>
            </Drawer>
        </Box>
    )
}
